(() => {
  if (customElements.get('cart-rewards')) return;

  // Both presentations consume this state; neither owns eligibility or tier logic.
  function getState(config) {
    const tiers = config.tiers
      .filter((tier) => Number.isFinite(tier.threshold) && tier.threshold > 0 && tier.label?.trim())
      .sort((a, b) => a.threshold - b.threshold)
      .filter((tier, index, sorted) => index === 0 || tier.threshold !== sorted[index - 1].threshold);
    const total = Math.max(0, config.cart.eligible_total);
    const qualifyingTotal = Math.max(0, config.cart.eligible_subtotal ?? config.cart.eligible_total);
    // Shipping uses the discounted cart total; discount tiers use eligible subtotals.
    const shippingTotal = Math.max(0, config.cart.total_price);
    const tierTotals = tiers.map((tier) => /^free shipping$/i.test(tier.label.trim()) ? shippingTotal : qualifyingTotal);
    const available = config.exclusionConfigured && tiers.length > 0
      && config.cart.currency === config.currency && Number.isFinite(total) && Number.isFinite(qualifyingTotal) && Number.isFinite(shippingTotal);
    const nextIndex = tiers.findIndex((tier, index) => tierTotals[index] < tier.threshold);
    const next = tiers[nextIndex];
    const unlocked = tiers.filter((tier, index) => tierTotals[index] >= tier.threshold).at(-1);
    const nextTotal = next ? tierTotals[nextIndex] : qualifyingTotal;
    const progressToNext = next ? Math.floor(nextTotal / next.threshold * 100) : 100;
    // Figma uses evenly spaced milestones, so interpolate within each segment.
    const previousPosition = nextIndex > 0 ? (nextIndex - 0.5) / tiers.length : 0;
    const nextPosition = (nextIndex + 0.5) / tiers.length;
    // Interpolate against the previous threshold using the same spend basis.
    // The first discount starts at $0, independently of the shipping threshold.
    const nextIsShipping = next && /^free shipping$/i.test(next.label.trim());
    const previousThreshold = nextIndex > 0
      ? tiers.slice(0, nextIndex).filter((tier) => /^free shipping$/i.test(tier.label.trim()) === nextIsShipping).at(-1)?.threshold || 0
      : 0;
    const segment = next ? Math.max(0, Math.min(1, (nextTotal - previousThreshold) / (next.threshold - previousThreshold))) : 1;
    const progress = next ? (previousPosition + segment * (nextPosition - previousPosition)) * 100 : 100;
    return { available, total, qualifyingTotal, shippingTotal, tierTotals, tiers, next, nextTotal, unlocked, segment, progressToNext, progress, remaining: next ? next.threshold - nextTotal : 0 };
  }

  class RewardsStore {
    listeners = new Set();

    subscribe(view, config) {
      this.listeners.add(view);
      if (this.listeners.size === 1) this.connect();
      this.accept(config);
      return () => {
        this.listeners.delete(view);
        if (!this.listeners.size) this.disconnect();
      };
    }

    connect() {
      this.onUpdate = ({ cart } = {}) => {
        if (!cart || cart.errors || cart === this.lastCart) return;
        this.lastCart = cart;
        this.cancelRefresh();
        // The cart drawer/page already arrives in bundled section responses.
        for (const html of Object.values(cart.sections || {})) {
          const config = this.parse(html);
          if (config) {
            this.accept(config);
            return;
          }
        }
        this.refresh();
      };
      this.onDocumentUpdate = (event) => this.onUpdate(event.detail);
      this.onRefresh = () => this.refresh();
      this.unsubscribe = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onUpdate);
      document.addEventListener('cart:updated', this.onDocumentUpdate);
      document.addEventListener('cart:refresh', this.onRefresh);
    }

    disconnect() {
      this.unsubscribe?.();
      document.removeEventListener('cart:updated', this.onDocumentUpdate);
      document.removeEventListener('cart:refresh', this.onRefresh);
      this.cancelRefresh();
      this.lastCart = null;
    }

    cancelRefresh() {
      this.controller?.abort();
      clearTimeout(this.timer);
    }

    parse(html) {
      if (typeof html !== 'string') return null;
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-rewards-data]');
      return node ? JSON.parse(node.textContent) : null;
    }

    accept(config) {
      this.config = config;
      const state = getState(config);
      this.listeners.forEach((view) => view.render(state, config));
    }

    refresh() {
      this.cancelRefresh();
      this.timer = setTimeout(async () => {
        const view = this.listeners.values().next().value;
        if (!view) return;
        const controller = new AbortController();
        this.controller = controller;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', view.rewardConfig.sectionId);
          const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
          if (!response.ok) throw new Error('Unable to refresh rewards');
          const config = this.parse(await response.text());
          if (!config) throw new Error('Missing reward data');
          if (!controller.signal.aborted && this.listeners.size) this.accept(config);
        } catch (error) {
          if (controller.signal.aborted) return;
          this.listeners.forEach((subscriber) => subscriber.setAvailable(false));
        }
      }, 50);
    }
  }

  const store = new RewardsStore();
  theme.cartRewards = { getState };

  customElements.define('cart-rewards', class CartRewards extends HTMLElement {
    connectedCallback() {
      this.rewardConfig = JSON.parse(this.querySelector('[data-cart-rewards-data]').textContent);
      this.unsubscribe = store.subscribe(this, this.rewardConfig);
      if (this.dataset.view === 'desktop') {
        this.onRewardResize = () => this.updateTooltip();
        this.tooltipObserver = new ResizeObserver(this.onRewardResize);
        this.tooltipObserver.observe(this);
        this.tooltipObserver.observe(this.querySelector('.desktop-mini-cart__tooltip'));
        window.addEventListener('resize', this.onRewardResize);
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.tooltipObserver?.disconnect();
      if (this.onRewardResize) window.removeEventListener('resize', this.onRewardResize);
    }

    updateTooltip() {
      if (this.hidden || !this.rewardState) return;
      const bounds = this.getBoundingClientRect();
      if (!bounds.width) return;
      const index = this.rewardState.next ? this.rewardState.tiers.indexOf(this.rewardState.next) : this.rewardState.tiers.length - 1;
      const tier = this.querySelector('[data-reward-tiers]').children[index];
      if (!tier) return;
      const target = tier.getBoundingClientRect();
      const tooltip = this.querySelector('.desktop-mini-cart__tooltip');
      const width = tooltip.getBoundingClientRect().width;
      const center = target.left + target.width / 2 - bounds.left;
      const drawer = this.closest('.drawer__inner')?.getBoundingClientRect();
      const min = (drawer?.left || 0) + 8 - bounds.left;
      const max = (drawer?.right || document.documentElement.clientWidth) - 8 - bounds.left - width;
      const left = Math.max(min, Math.min(center - width / 2, max));
      this.style.setProperty('--mini-tooltip-left', `${left}px`);
      this.style.setProperty('--mini-tooltip-arrow', `${center - left}px`);
    }

    setAvailable(available) {
      this.hidden = !available;
      if (this.dataset.view === 'desktop') {
        this.dispatchEvent(new CustomEvent('rewards:availability', { bubbles: true, detail: { available } }));
      }
      if (this.dataset.view === 'dock') {
        this.closest('.dock__item')?.classList.toggle('dock__item--rewards', available);
      }
    }

    render(state, config) {
      this.setAvailable(state.available);
      if (!state.available) return;
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: config.currency,
      });
      const money = (cents) => formatter.format(cents / 100);
      const nextStatus = state.next
        ? `${state.next.label}: ${money(state.nextTotal)} / ${money(state.next.threshold)}`
        : config.complete;
      const track = this.querySelector('[role="progressbar"]');
      track.setAttribute('aria-valuenow', Math.round(this.dataset.view === 'dock' ? state.progressToNext : state.progress));
      track.setAttribute('aria-valuetext', nextStatus);
      track.setAttribute('aria-label', state.next?.label || config.complete);
      if (this.dataset.view === 'dock') this.renderDock(state, config, nextStatus);
      else this.renderCart(state, config, money);
      if (this.dataset.view === 'desktop') {
        this.rewardState = state;
        this.updateTooltip();
        this.dispatchEvent(new CustomEvent('rewards:render', { bubbles: true, detail: { state, config } }));
      }
    }

    renderDock(state, config, nextStatus) {
      this.classList.toggle('is-complete', !state.next);
      this.querySelector('.dock-rewards__bubble').hidden = !state.unlocked;
      this.querySelector('[data-reward-unlocked]').textContent = state.unlocked?.label || '';
      this.querySelector('[data-reward-caption]').textContent = config.unlocked;
      const target = this.querySelector('[data-reward-target]');
      const label = state.next?.label || config.complete;
      const parts = state.next
        ? label.trim().match(/^(.*?)\s+(off|shipping)$/i)
        : label.trim().match(/^(\S+)\s+(.+)$/);
      if (parts) {
        const caption = document.createElement('span');
        caption.className = 'dock-rewards__target-caption';
        caption.textContent = parts[2];
        target.replaceChildren(document.createTextNode(parts[1]), caption);
      } else target.textContent = label;
      this.style.setProperty('--reward-progress', `${state.progressToNext}%`);
      this.querySelector('[data-reward-status]').textContent = [
        state.unlocked && `${state.unlocked.label} ${config.unlocked}`, nextStatus,
      ].filter(Boolean).join('. ');
    }

    renderCart(state, config, money) {
      const message = this.querySelector('[data-reward-message]');
      const signature = JSON.stringify([state.remaining, state.next?.label, config.remaining, config.complete, config.currency]);
      if (signature !== this.messageSignature) {
        const fragment = document.createDocumentFragment();
        if (state.next) {
          (config.remaining || "You're [amount] away from [reward]").split(/(\[amount\]|\[reward\])/).forEach((part) => {
            if (part === '[amount]' || part === '[reward]') {
              const strong = document.createElement('strong');
              if (part === '[reward]') strong.className = 'cart-rewards__next-reward';
              strong.textContent = part === '[amount]' ? money(state.remaining) : state.next.label;
              fragment.append(strong);
            } else fragment.append(document.createTextNode(part));
          });
        } else fragment.append(document.createTextNode(config.complete));
        message.replaceChildren(fragment);
        this.messageSignature = signature;
      }

      const tiersSignature = JSON.stringify([state.tiers, config.ordersLabel, config.currency]);
      const list = this.querySelector('[data-reward-tiers]');
      if (tiersSignature !== this.tiersSignature) {
        const fragment = document.createDocumentFragment();
        state.tiers.forEach((tier) => {
          const item = document.createElement('li');
          item.className = 'cart-rewards__tier';
          const label = document.createElement('span');
          label.className = 'cart-rewards__label';
          label.textContent = tier.label.replace(/^free shipping$/i, this.dataset.view === 'desktop' ? 'Free Shipping' : 'Free\nShipping');
          const dot = document.createElement('span');
          dot.className = 'cart-rewards__dot';
          dot.setAttribute('aria-hidden', 'true');
          const threshold = document.createElement('span');
          threshold.className = 'cart-rewards__threshold';
          const value = money(tier.threshold).replace(/([.,])00(?=\D*$)/, '');
          threshold.textContent = `${config.ordersLabel} ${value}+`;
          item.append(label, dot, threshold);
          fragment.append(item);
        });
        list.replaceChildren(fragment);
        this.tiersSignature = tiersSignature;
      }
      [...list.children].forEach((item, index) => {
        const tier = state.tiers[index];
        const unlocked = state.tierTotals[index] >= tier.threshold;
        const shipping = /^free shipping$/i.test(tier.label.trim());
        const current = tier === state.unlocked;
        item.classList.toggle('is-unlocked', unlocked);
        item.classList.toggle('is-shipping', shipping);
        item.classList.toggle('is-current', unlocked && current);
        item.classList.toggle('is-superseded', unlocked && !shipping && !current);
        item.setAttribute('aria-label', `${state.tiers[index].label}, ${money(state.tiers[index].threshold)}${unlocked ? `, ${config.unlocked}` : ''}`);
      });
      this.style.setProperty('--reward-tier-count', state.tiers.length);
      // Match the rail to the inset milestone centers, including partial segments.
      let progress = '100%';
      if (state.next) {
        const nextIndex = state.tiers.indexOf(state.next);
        const fraction = state.segment;
        progress = nextIndex === 0
          ? `calc(var(--reward-edge) * ${fraction})`
          : `calc(var(--reward-edge) + (100% - var(--reward-edge) * 2) * ${(nextIndex - 1 + fraction) / (state.tiers.length - 1)})`;
      }
      this.style.setProperty('--reward-progress', progress);
    }
  });
})();
